'use client';

import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

import { FeaturedBadge } from '@/components/listings/FeaturedBadge';
import type { FeaturedBadgeVariant, FeaturedPlacementSummary } from '@/lib/api/clients/featured-content';
import { isHeroPlacement } from '@/lib/api/clients/featured-content';
import type { Property } from '@/lib/api/clients/properties';
import type { HeroProps } from '@routeroyal/theme-core';
import { GrainTexture } from './GrainTexture';
import { FormatPrice } from '@/components/ui/FormatPrice';
import { DEFAULT_CURRENCY } from '@/lib/config/app.config';
import { extractBestPriceSimple } from '@/lib/utils/price';

/** Property with optional featured data for hero display */
interface PropertyWithFeatured extends Property {
  featuredPlacements?: FeaturedPlacementSummary[];
}

/**
 * Get only featured properties that are specifically marked for HERO placement.
 * Does NOT fall back to other placement types - only shows Hero placements in the Hero.
 */
function getFeaturedPropertiesForHero(properties: PropertyWithFeatured[], maxCount: number = 5): PropertyWithFeatured[] {
  // Filter to only properties with featured placements
  const featuredProperties = properties.filter(p => 
    p.featuredPlacements && p.featuredPlacements.length > 0
  );
  
  if (featuredProperties.length === 0) {
    return [];
  }
  
  // Filter to ONLY properties with Hero placement types (HERO, HERO_CAROUSEL)
  // No fallback to other placement types - Hero shows only Hero placements
  const heroProperties = featuredProperties.filter(p =>
    p.featuredPlacements?.some(fp => isHeroPlacement(fp.placementType))
  );
  
  // Sort by priority (highest first) - prioritize hero-specific placements
  const sortByPriority = (a: PropertyWithFeatured, b: PropertyWithFeatured) => {
    const getPriority = (prop: PropertyWithFeatured) => {
      if (!prop.featuredPlacements) return 0;
      // Find the hero placement and get its priority
      const heroPlacement = prop.featuredPlacements.find(fp => isHeroPlacement(fp.placementType));
      return heroPlacement?.priority || 0;
    };
    
    return getPriority(b) - getPriority(a);
  };
  
  // Return ONLY hero properties - no fallback to other placement types
  return heroProperties.sort(sortByPriority).slice(0, maxCount);
}

/** Property value with nested attribute from the API */
interface PropertyValueWithAttribute {
  id: string;
  propertyId: string;
  attributeId: string;
  stringValue?: string | null;
  numberValue?: number | null;
  booleanValue?: boolean | null;
  attribute?: {
    id: string;
    code: string;
    name: string;
    valueType: string;
  };
}

// Helper to extract value from propertyValues array
function getPropertyValue(property: PropertyWithFeatured, attributeCode: string): number | null {
  // Cast through unknown first to handle the type mismatch
  const propertyValues = (property as unknown as { propertyValues?: PropertyValueWithAttribute[] }).propertyValues;
  
  if (!propertyValues) return null;
  
  // Find by attribute.code (case-insensitive)
  const value = propertyValues.find(pv => 
    pv.attribute?.code?.toLowerCase() === attributeCode.toLowerCase()
  );
  
  if (value?.numberValue !== undefined && value.numberValue !== null) {
    return value.numberValue;
  }
  if (value?.stringValue) {
    const parsed = parseFloat(value.stringValue);
    return isNaN(parsed) ? null : parsed;
  }
  return null;
}

// Helper to format property for display
function formatPropertyForHero(property: PropertyWithFeatured) {
  const bestPrice = extractBestPriceSimple(property);
  const askingPrice = bestPrice.amount;
  const currency = bestPrice.currency;
  
  // Get bedrooms - check direct property first, then propertyValues
  const bedrooms = property.bedrooms ?? getPropertyValue(property, 'bedrooms') ?? getPropertyValue(property, 'BEDROOMS') ?? 0;
  
  // Get bathrooms - check direct property first, then propertyValues
  const bathrooms = property.bathrooms ?? getPropertyValue(property, 'bathrooms') ?? getPropertyValue(property, 'BATHROOMS') ?? 0;
  
  // Get floor area - check direct property first, then propertyValues
  const floorArea = property.totalFloorArea ?? getPropertyValue(property, 'totalFloorArea') ?? getPropertyValue(property, 'TOTAL_FLOOR_AREA');
  const areaUnit = property.totalFloorAreaUnit || 'SQM';
  const area = floorArea 
    ? `${floorArea} ${areaUnit === 'SQM' ? 'm²' : 'sqft'}`
    : null;
  
  // Get primary featured badge if available
  const primaryFeatured = property.featuredPlacements?.find(p => p.badgeVariant);

  return {
    id: property.id,
    image: property.primaryImageUrl || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&h=1080&fit=crop',
    name: property.name,
    bedrooms: bedrooms,
    bathrooms: bathrooms,
    area: area,
    askingPrice: askingPrice ?? null,
    currency: currency,
    badgeVariant: primaryFeatured?.badgeVariant || null,
    badgeLabel: primaryFeatured?.label || null,
  };
}

// Helper to safely get string from messages
const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

/**
 * Allys Ultra-Modern Hero Component
 * 
 * Features:
 * - Cinematic image slider showing ONLY featured properties
 * - Prioritizes HERO_CAROUSEL placements, falls back to other featured types
 * - Displays featured badges on properties
 * - Modern animations styled like Apple TV/Netflix
 * 
 * Note: Only properties with featured placements are displayed.
 * If no featured properties exist, a placeholder is shown.
 */
export function AllysHero({ 
  title, 
  subtitle, 
  backgroundImage,
  ctaText: _ctaText,
  ctaLink: _ctaLink,
  messages,
  locale,
  data,
}: HeroProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [scrollY, setScrollY] = useState(0);
  const [selectedType, setSelectedType] = useState('');
  const [selectedBeds, setSelectedBeds] = useState('');
  const [selectedBaths, setSelectedBaths] = useState('');
  const router = useRouter();

  // Parallax scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Get translations from messages
  const allysHero = messages?.allysHero as Record<string, unknown> | undefined;
  const heroTitle = title || safeGetString(allysHero?.title, 'Discover Exceptional Mauritian Real Estate');
  const heroSubtitle = subtitle || safeGetString(allysHero?.subtitle, 'Your trusted partner for premium properties in Mauritius. From stunning coastal villas to modern apartments, we\'ll lead you the way home.');

  // Get property search translations
  const propertySearch = messages?.propertySearch as Record<string, unknown> | undefined;
  const searchLabels = propertySearch?.labels as Record<string, unknown> | undefined;
  const searchOptions = propertySearch?.options as Record<string, unknown> | undefined;
  const propertyTypes = searchOptions?.propertyTypes as Record<string, string> | undefined;
  const bedroomCounts = searchOptions?.bedroomCounts as Record<string, string> | undefined;
  const bathroomCounts = searchOptions?.bathroomCounts as Record<string, string> | undefined;

  const t = {
    type: safeGetString(searchLabels?.type, 'Type'),
    beds: safeGetString(searchLabels?.bedrooms, 'Beds'),
    baths: safeGetString(searchLabels?.bathrooms, 'Baths'),
    search: safeGetString(searchLabels?.searchButton, 'Search'),
    all: safeGetString(searchOptions?.all, 'All'),
    any: safeGetString(searchOptions?.any, 'Any'),
    types: {
      house: safeGetString(propertyTypes?.house, 'House'),
      apartment: safeGetString(propertyTypes?.apartment, 'Apartment'),
      villa: safeGetString(propertyTypes?.villa, 'Villa'),
      land: safeGetString(propertyTypes?.land, 'Land'),
      penthouse: safeGetString(propertyTypes?.penthouse, 'Penthouse'),
    },
    bedrooms: {
      '1plus': safeGetString(bedroomCounts?.['1plus'], '1+'),
      '2plus': safeGetString(bedroomCounts?.['2plus'], '2+'),
      '3plus': safeGetString(bedroomCounts?.['3plus'], '3+'),
      '4plus': safeGetString(bedroomCounts?.['4plus'], '4+'),
      '5plus': safeGetString(bedroomCounts?.['5plus'], '5+'),
    },
    bathrooms: {
      '1plus': safeGetString(bathroomCounts?.['1plus'], '1+'),
      '2plus': safeGetString(bathroomCounts?.['2plus'], '2+'),
      '3plus': safeGetString(bathroomCounts?.['3plus'], '3+'),
      '4plus': safeGetString(bathroomCounts?.['4plus'], '4+'),
    },
  };
  
  // Get properties from data
  const allProperties = (data?.resources as PropertyWithFeatured[]) || [];
  
  
  // Get ONLY featured properties for the hero carousel
  // Prioritizes HERO_CAROUSEL placement, falls back to other featured placements
  const featuredOnlyProperties = getFeaturedPropertiesForHero(allProperties, 5);
  
  // Format featured properties for display
  const formattedFeaturedProperties = featuredOnlyProperties.map(formatPropertyForHero);
  
  // Use featured properties if available, fallback to placeholder
  // NOTE: We intentionally show only featured listings - no random fallback
  const displayProperties = formattedFeaturedProperties.length > 0 ? formattedFeaturedProperties : [
    {
      id: 'placeholder',
      image: backgroundImage || "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&h=1080&fit=crop",
      name: "Featured Properties Coming Soon",
      bedrooms: 0,
      bathrooms: 0,
      area: null,
      askingPrice: null,
      currency: DEFAULT_CURRENCY,
      badgeVariant: null,
      badgeLabel: null,
    }
  ];

  // Auto-rotate images every 6 seconds
  useEffect(() => {
    if (!isAutoPlaying || displayProperties.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % displayProperties.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, displayProperties.length]);

  const handlePreviousImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + displayProperties.length) % displayProperties.length);
    setIsAutoPlaying(false);
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % displayProperties.length);
    setIsAutoPlaying(false);
  };

  const handleIndicatorClick = (index: number) => {
    setCurrentImageIndex(index);
    setIsAutoPlaying(false);
  };

  const currentProperty = displayProperties[currentImageIndex] || displayProperties[0];

  return (
    <section className="relative h-dvh w-full overflow-hidden bg-[var(--color-background)]" aria-label="Hero section" id="hero-section">
      {/* Image Slider with Parallax */}
      <div className="absolute inset-0 overflow-hidden">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={currentImageIndex}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1.05 }}
            exit={{ opacity: 0, scale: 1 }}
            transition={{ duration: 1.5, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="absolute inset-0"
            style={{
              // Parallax effect: image moves slower than scroll
              transform: `translateY(${scrollY * 0.4}px) scale(${1.05 + scrollY * 0.0002})`,
            }}
          >
            {(() => {
              // Prioritize property images over static backgroundImage
              const imageSrc = currentProperty?.image || backgroundImage;
              return imageSrc ? (
                <Image
                  src={imageSrc}
                  alt={`Featured property ${currentImageIndex + 1}`}
                  fill
                  className="object-cover"
                  priority={currentImageIndex === 0}
                  sizes="100vw"
                />
              ) : null;
            })()}
          </motion.div>
        </AnimatePresence>
        {/* Refined gradient overlay for depth - outside motion div for stable gradients */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-black/10 to-black/70 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />
        <GrainTexture />
      </div>

      {/* Navigation Arrows - Subtle style */}
      {displayProperties.length > 1 && (
        <>
          <button
            onClick={handlePreviousImage}
            className="absolute left-6 lg:left-8 top-1/2 -translate-y-1/2 z-30 w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center hover:bg-white/20 transition-all duration-200 group"
            aria-label="Previous image"
          >
            <svg className="w-5 h-5 text-white/90 group-hover:text-white transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            onClick={handleNextImage}
            className="absolute right-6 lg:right-8 top-1/2 -translate-y-1/2 z-30 w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center hover:bg-white/20 transition-all duration-200 group"
            aria-label="Next image"
          >
            <svg className="w-5 h-5 text-white/90 group-hover:text-white transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* Image Indicators - Minimal with proper touch targets */}
      {displayProperties.length > 1 && (
        <div className="absolute bottom-8 lg:bottom-10 left-1/2 -translate-x-1/2 z-30 flex gap-2">
          {displayProperties.map((_, index) => (
            <button
              key={index}
              onClick={() => handleIndicatorClick(index)}
              className="relative group min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label={`Go to property ${index + 1}`}
          >
            <div className={`h-1 rounded-full transition-all duration-300 ${
              index === currentImageIndex ? 'w-8 bg-white' : 'w-1.5 bg-white/50 group-hover:bg-white/70'
            }`} />
          </button>
        ))}
        </div>
      )}

      {/* Content Overlay */}
      <div className="relative z-20 h-full">
        {/* Main Content - Heading positioned center-left, pt accounts for nav overlay */}
        <div className="absolute inset-0 flex flex-col justify-center pt-20 lg:pt-24 px-6 sm:px-8 lg:px-16">
          <div className="w-full max-w-7xl mx-auto">
            {/* Heading & Subheading — h1 renders immediately for fast LCP */}
            <div className="text-left max-w-2xl">
          <h1
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-heading font-bold text-white mb-3 lg:mb-5 tracking-[-0.05em] leading-[0.95] [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]"
                style={{
                  textShadow: '0 4px 60px rgba(0,0,0,0.5), 0 2px 30px rgba(0,0,0,0.4)'
                }}
          >
                {heroTitle}
          </h1>
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="hero-description text-base sm:text-lg md:text-xl lg:text-2xl font-body text-white/90 font-normal leading-relaxed max-w-lg [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]"
                data-speakable
                style={{
                  textShadow: '0 2px 30px rgba(0,0,0,0.5)'
                }}
          >
                {heroSubtitle}
          </motion.p>

          {/* Wreath Icon and Five Stars */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="mt-4 lg:mt-6 flex items-center gap-4"
          >
            {/* Wreath Icon */}
            <div className="relative w-12 h-12 flex-shrink-0">
              <Image
                src="https://res.cloudinary.com/elementary-media/image/upload/v1762189641/olive-wreath-icon_mxmqkd.svg"
                alt="Premium Quality"
                width={48}
                height={48}
                className="opacity-90"
                style={{
                  filter: 'brightness(0) saturate(100%) invert(100%) drop-shadow(0 2px 20px rgba(0,0,0,0.5))'
                }}
              />
            </div>

            {/* Five Stars */}
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <svg
                  key={star}
                  className="w-5 h-5 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  style={{
                    filter: 'drop-shadow(0 2px 15px rgba(0,0,0,0.5))'
                  }}
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
          </motion.div>

          {/* Property Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.0, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="mt-4 lg:mt-6 max-w-xl"
          >
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const params = new URLSearchParams();
                if (selectedType) params.set('type', selectedType);
                if (selectedBeds) params.set('beds', selectedBeds);
                if (selectedBaths) params.set('baths', selectedBaths);
                const qs = params.toString();
                const href = qs
                  ? `/${locale || 'en'}/properties?${qs}`
                  : `/${locale || 'en'}/properties`;
                router.push(href);
              }}
              className="relative bg-black/30 backdrop-blur-2xl rounded-xl border border-white/15 shadow-2xl overflow-hidden transition-all duration-300 focus-within:bg-black/40 focus-within:border-white/25"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/[0.08] via-transparent to-white/[0.03] pointer-events-none" />
              <div className="relative grid grid-cols-1 sm:grid-cols-3 gap-px">
                {/* Type Selector */}
                <div className="px-3 sm:px-4 py-3">
                  <label htmlFor="hero-type" className="block text-xs font-medium text-white/50 uppercase tracking-wider mb-1 [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
                    {t.type}
                  </label>
                  <div className="relative">
                    <select
                      id="hero-type"
                      value={selectedType}
                      onChange={(e) => setSelectedType(e.target.value)}
                      className="w-full bg-transparent text-white text-sm font-medium appearance-none cursor-pointer pr-5 focus:outline-none [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]"
                    >
                      <option value="" className="bg-[#1a1a2e] text-white">{t.all}</option>
                      <option value="villa" className="bg-[#1a1a2e] text-white">{t.types.villa}</option>
                      <option value="apartment" className="bg-[#1a1a2e] text-white">{t.types.apartment}</option>
                      <option value="house" className="bg-[#1a1a2e] text-white">{t.types.house}</option>
                      <option value="land" className="bg-[#1a1a2e] text-white">{t.types.land}</option>
                      <option value="penthouse" className="bg-[#1a1a2e] text-white">{t.types.penthouse}</option>
                    </select>
                    <svg className="absolute right-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Beds Selector */}
                <div className="px-3 sm:px-4 py-3 border-t sm:border-t-0 sm:border-l border-white/10">
                  <label htmlFor="hero-beds" className="block text-xs font-medium text-white/50 uppercase tracking-wider mb-1 [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
                    {t.beds}
                  </label>
                  <div className="relative">
                    <select
                      id="hero-beds"
                      value={selectedBeds}
                      onChange={(e) => setSelectedBeds(e.target.value)}
                      className="w-full bg-transparent text-white text-sm font-medium appearance-none cursor-pointer pr-5 focus:outline-none [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]"
                    >
                      <option value="" className="bg-[#1a1a2e] text-white">{t.any}</option>
                      <option value="1" className="bg-[#1a1a2e] text-white">{t.bedrooms['1plus']}</option>
                      <option value="2" className="bg-[#1a1a2e] text-white">{t.bedrooms['2plus']}</option>
                      <option value="3" className="bg-[#1a1a2e] text-white">{t.bedrooms['3plus']}</option>
                      <option value="4" className="bg-[#1a1a2e] text-white">{t.bedrooms['4plus']}</option>
                      <option value="5" className="bg-[#1a1a2e] text-white">{t.bedrooms['5plus']}</option>
                    </select>
                    <svg className="absolute right-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Baths Selector */}
                <div className="px-3 sm:px-4 py-3 border-t sm:border-t-0 sm:border-l border-white/10">
                  <label htmlFor="hero-baths" className="block text-xs font-medium text-white/50 uppercase tracking-wider mb-1 [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
                    {t.baths}
                  </label>
                  <div className="relative">
                    <select
                      id="hero-baths"
                      value={selectedBaths}
                      onChange={(e) => setSelectedBaths(e.target.value)}
                      className="w-full bg-transparent text-white text-sm font-medium appearance-none cursor-pointer pr-5 focus:outline-none [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]"
                    >
                      <option value="" className="bg-[#1a1a2e] text-white">{t.any}</option>
                      <option value="1" className="bg-[#1a1a2e] text-white">{t.bathrooms['1plus']}</option>
                      <option value="2" className="bg-[#1a1a2e] text-white">{t.bathrooms['2plus']}</option>
                      <option value="3" className="bg-[#1a1a2e] text-white">{t.bathrooms['3plus']}</option>
                      <option value="4" className="bg-[#1a1a2e] text-white">{t.bathrooms['4plus']}</option>
                    </select>
                    <svg className="absolute right-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Search Button - Full width below selectors */}
              <div className="relative px-3 sm:px-4 pb-3">
                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#BBA339] to-[#E4CE4A] rounded-lg text-sm font-semibold text-[#243462] hover:shadow-lg hover:shadow-[#BBA339]/30 transition-all duration-300 [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  {t.search}
                </button>
              </div>
            </form>
          </motion.div>
            </div>
          </div>
        </div>

        {/* Bottom Right: Featured Property Card with Parallax */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentImageIndex}
            initial={{ opacity: 0, y: 40, x: 20 }}
            animate={{ opacity: 1, y: 0, x: 0 }}
            exit={{ opacity: 0, y: 20, x: 20 }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="absolute bottom-8 right-6 sm:bottom-12 sm:right-8 lg:bottom-16 lg:right-16 z-30 hidden md:block"
            style={{
              // Subtle parallax - card moves slightly with scroll
              transform: `translateY(${scrollY * -0.15}px)`,
            }}
          >
            {currentProperty && currentProperty.id !== 'placeholder' && (
              <div className="relative group">
                {/* Glassmorphic Card */}
                <div className="relative bg-black/30 backdrop-blur-2xl rounded-2xl border border-white/15 p-5 lg:p-6 shadow-2xl overflow-hidden min-w-[280px] max-w-[340px] transition-all duration-500 group-hover:bg-black/40 group-hover:border-white/25">
                  {/* Animated gradient background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/[0.08] via-transparent to-white/[0.03] pointer-events-none" />
                  <div className="absolute -inset-px bg-gradient-to-t from-white/5 via-transparent to-transparent rounded-2xl pointer-events-none" />
                  
                  {/* Content */}
                  <div className="relative z-10 space-y-3">
                    {/* Featured Badge */}
                    {currentProperty.badgeVariant && (
                      <div className="mb-1">
                        <FeaturedBadge
                          variant={currentProperty.badgeVariant as FeaturedBadgeVariant}
                          label={currentProperty.badgeLabel}
                          size="sm"
                        />
                      </div>
                    )}
                    
                    {/* Label with animated line */}
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-px bg-gradient-to-r from-white/50 to-white/20 group-hover:w-8 transition-all duration-300" />
                      <span className="text-xs font-semibold tracking-[0.25em] uppercase text-white/50">Featured</span>
                    </div>
                    
                    {/* Property Name */}
                    <h2 className="text-lg lg:text-xl font-semibold text-white leading-tight tracking-tight line-clamp-2">
                      {currentProperty.name}
                    </h2>
                    
                    {/* Property Stats Row */}
                    {(currentProperty.bedrooms > 0 || currentProperty.bathrooms > 0 || currentProperty.area) && (
                      <div className="flex items-center gap-3 text-white/60 text-xs pt-1">
                        {currentProperty.bedrooms > 0 && (
                          <div className="flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
                            </svg>
                            <span>{currentProperty.bedrooms}</span>
                          </div>
                        )}
                        {currentProperty.bathrooms > 0 && (
                          <div className="flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{currentProperty.bathrooms}</span>
                          </div>
                        )}
                        {currentProperty.area && (
                          <div className="flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                            </svg>
                            <span>{currentProperty.area}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Price with emphasis - Now uses FormatPrice for currency conversion */}
                    {currentProperty.askingPrice && currentProperty.askingPrice > 0 && (
                      <div className="pt-1">
                        <p className="text-2xl lg:text-3xl font-bold text-white tracking-tight">
                          <FormatPrice
                            amount={currentProperty.askingPrice}
                            currency={currentProperty.currency}
                            size="xl"
                            className="!text-white"
                          />
                        </p>
                      </div>
                    )}
                    
                    {/* View Property Link */}
                    <div className="pt-1">
                      <a 
                        href={`/${locale || 'en'}/properties/${currentProperty.id}`}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-white/70 hover:text-white transition-colors group/link"
                      >
                        <span>View Details</span>
                        <svg className="w-3.5 h-3.5 transition-transform group-hover/link:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                        </svg>
                      </a>
                    </div>
                  </div>
                  
                  {/* Decorative elements */}
                  <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-white/[0.08] to-transparent rounded-bl-3xl pointer-events-none" />
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-white/[0.03] to-transparent rounded-tr-3xl pointer-events-none" />
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Additional vignette effect for dramatic look */}
      <div className="absolute inset-0 pointer-events-none z-10" style={{
        background: 'radial-gradient(ellipse at center, transparent 0%, transparent 40%, rgba(0,0,0,0.3) 100%)'
      }} />
    </section>
  );
}
